import React from 'react';
import { Shield, User, LogOut, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AppSectionSelectorProps {
  userName: string;
  onSelectAdmin: () => void;
  onSelectCandidate: () => void;
  onLogout: () => void;
}

export const AppSectionSelector: React.FC<AppSectionSelectorProps> = ({
  userName,
  onSelectAdmin,
  onSelectCandidate,
  onLogout,
}) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-aero-50 flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-10">
        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold text-2xl shadow-lg transform skew-x-[-10deg]">
          <span className="transform skew-x-[10deg]">S</span>
        </div>
        <span className="font-bold text-2xl tracking-tight text-aero-900">SoloReady</span>
      </div>

      {/* Greeting */}
      <p className="text-aero-500 text-sm mb-2">{t('appSelector.subtitle')}</p>
      <h1 className="text-2xl font-bold text-aero-900 mb-8">
        {t('appSelector.title')}, {userName}
      </h1>

      {/* Section cards */}
      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xl">
        {/* Admin card */}
        <button
          onClick={onSelectAdmin}
          className="flex-1 group p-6 bg-white rounded-xl border-2 border-aero-200 hover:border-aero-500 hover:shadow-lg transition-all text-left"
        >
          <div className="w-12 h-12 bg-aero-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-aero-600 transition-colors">
            <Shield className="w-6 h-6 text-aero-600 group-hover:text-white transition-colors" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-aero-900">{t('appSelector.adminCard')}</h2>
              <p className="text-sm text-aero-500 mt-1">{t('appSelector.adminDesc')}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-aero-300 group-hover:text-aero-600 transition-colors ml-4 flex-shrink-0" />
          </div>
        </button>

        {/* Candidate card */}
        <button
          onClick={onSelectCandidate}
          className="flex-1 group p-6 bg-white rounded-xl border-2 border-aero-200 hover:border-green-500 hover:shadow-lg transition-all text-left"
        >
          <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500 transition-colors">
            <User className="w-6 h-6 text-green-600 group-hover:text-white transition-colors" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-aero-900">{t('appSelector.candidateCard')}</h2>
              <p className="text-sm text-aero-500 mt-1">{t('appSelector.candidateDesc')}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-aero-300 group-hover:text-green-500 transition-colors ml-4 flex-shrink-0" />
          </div>
        </button>
      </div>

      {/* Logout */}
      <button
        onClick={onLogout}
        className="mt-8 flex items-center gap-2 text-sm text-aero-400 hover:text-red-500 transition-colors"
      >
        <LogOut className="w-4 h-4" />
        {t('common.logout')}
      </button>
    </div>
  );
};
